import { createRouter, useLocation, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { routeTree } from "./routeTree.gen";

function safeSerialize(value: unknown) {
  try {
    return JSON.stringify(
      value,
      (_key, current) => {
        if (typeof current === "bigint") return current.toString();
        if (current instanceof Error) {
          return {
            name: current.name,
            message: current.message,
            stack: current.stack,
          };
        }
        return current;
      },
      2,
    );
  } catch {
    return "[unserializable]";
  }
}

function DefaultErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  const safeError = error instanceof Error ? error : new Error("Unexpected application error");
  const router = useRouter();
  const location = useLocation();

  useEffect(() => {
    console.error("[router:error-boundary]", {
      pathname: location.pathname,
      search: location.search,
      message: safeError.message,
      stack: safeError.stack,
      name: safeError.name,
      serialized: safeSerialize(safeError),
    });
  }, [safeError, location.pathname, location.search]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </div>
        <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">Something went wrong</h1>
        <p className="mt-2 text-ui-base text-muted-foreground">
          An unexpected error occurred. Please try again.
        </p>
        {safeError.message && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-ui-sm bg-muted p-3 text-left font-mono text-ui-sm text-destructive">
            {safeError.message}
          </pre>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-ui-sm bg-primary px-4 py-2 text-ui-base font-bold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-ui-sm border border-input bg-background px-4 py-2 text-ui-base font-bold text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const router = createRouter({
    routeTree,
    context: {},
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DefaultErrorComponent,
  });

  return router;
};
